/**
 * Commission progress: the maths and the wording behind the strip under the
 * Invoiced tile on the University Partnerships dashboard, and its modal.
 *
 * Owner decisions (2026-10-25):
 * - Three bars: Personal (my invoiced vs my personal target), Regional (the
 *   region's invoiced vs the regional target) and Contribution (my invoiced on
 *   companies in the region vs the REGIONAL target).
 * - Targets are the commission assignment's contractual full-year figures,
 *   never pro-rated; actuals are restated into the scheme's currency by the
 *   server. Nothing here converts or sums across currencies.
 * - A null target is "no commitment", not zero: the bar shows the actual with
 *   no fill, and pct / remaining are null (render `—`).
 *
 * Money is a decimal string end to end. All arithmetic is BigInt on scaled
 * integers; the only `number` produced is a whole percentage.
 */
import type { CommissionEarned, CommissionProgress, CommissionProgressRegion } from './types';

export interface ProgressBar {
  actual: string;
  target: string | null;
  /** round-half-up(actual / target × 100); null when target is null or ≤ 0. May exceed 100 or be negative. */
  pct: number | null;
  /** max(target − actual, 0) as a 2-dp string; null when target is null. */
  remaining: string | null;
}

interface Scaled {
  units: bigint;
  scale: number;
}

/** Parses '-1234.50'-shaped strings. Anything else is null, never NaN. */
function parse(value: string | null | undefined): Scaled | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

/** True when the decimal string parses and is above zero. */
function isPositive(value: string): boolean {
  const parsed = parse(value);
  return parsed !== null && parsed.units > 0n;
}

function rescale(value: Scaled, scale: number): bigint {
  return value.units * 10n ** BigInt(scale - value.scale);
}

/** Floor division for BigInt (BigInt `/` truncates toward zero). */
function floorDiv(a: bigint, b: bigint): bigint {
  const q = a / b;
  return (a % b !== 0n) && ((a < 0n) !== (b < 0n)) ? q - 1n : q;
}

/** Whole percentage part / whole × 100, rounded half up; null when whole ≤ 0 or either is unparseable. */
function percent(part: string, whole: string): number | null {
  const p = parse(part);
  const w = parse(whole);
  if (p === null || w === null) return null;
  const scale = Math.max(p.scale, w.scale);
  const num = rescale(p, scale) * 100n;
  const den = rescale(w, scale);
  if (den <= 0n) return null;
  // round half up: floor(num / den + 1/2) = floor((2·num + den) / (2·den))
  return Number(floorDiv(2n * num + den, 2n * den));
}

/** Renders scaled units at exactly 2 dp (rounding half away from zero if wider). */
function formatTwoDp(units: bigint, scale: number): string {
  let cents: bigint;
  if (scale <= 2) {
    cents = units * 10n ** BigInt(2 - scale);
  } else {
    const divisor = 10n ** BigInt(scale - 2);
    const negative = units < 0n;
    const abs = negative ? -units : units;
    const rounded = (abs * 2n + divisor) / (divisor * 2n);
    cents = negative ? -rounded : rounded;
  }
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, '0');
  const body = `${digits.slice(0, -2)}.${digits.slice(-2)}`;
  return negative ? `-${body}` : body;
}

export function progressBar(actual: string, target: string | null): ProgressBar {
  if (target === null) return { actual, target: null, pct: null, remaining: null };
  const a = parse(actual);
  const t = parse(target);
  let remaining: string | null = null;
  if (a !== null && t !== null) {
    const scale = Math.max(a.scale, t.scale, 2);
    const diff = rescale(t, scale) - rescale(a, scale);
    remaining = formatTwoDp(diff > 0n ? diff : 0n, scale);
  }
  return { actual, target, pct: percent(actual, target), remaining };
}

/** Width of a bar fill, clamped to 0..100; null is an empty track. */
export function barWidth(pct: number | null): number {
  if (pct === null || !Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, pct));
}

/** Integer share of part in whole, half up; null when whole ≤ 0. */
export function shareOf(part: string, whole: string): number | null {
  return percent(part, whole);
}

/** Contribution bar = my invoiced on the region's companies ÷ the REGIONAL target. */
export function contributionBar(progress: CommissionProgress): ProgressBar | null {
  if (progress.contribution === null || progress.regional === null) return null;
  return progressBar(progress.contribution.actual, progress.regional.target);
}

/** My share of one billing band's invoiced revenue. */
export function regionShare(region: CommissionProgressRegion): number | null {
  return shareOf(region.contribution.actual, region.regional.actual);
}

export const PROGRESS_LABELS = {
  personal: 'Personal target',
  regional: 'Regional target',
  contribution: 'My contribution to region',
} as const;

export const PROGRESS_MODAL_TITLE = 'Invoiced against commission targets';

export const PROGRESS_REGION_HEADINGS = {
  region: 'Region',
  regional: 'Region invoiced',
  contribution: 'My invoiced',
  share: 'My share',
} as const;

/*
 * Commission earned + remainder target (owner 2026-10-27).
 * - `earned` is the scheme maths on invoiced revenue to date against the
 *   full-year targets; the server computes it, nothing here recomputes it.
 * - A remainder ("derived") target belongs to a person with no commission
 *   assignment: personal row only, no `earned`.
 * - `undefined` on `kind` / `earned` means an older API: treat as an
 *   assignment row with nothing earned to show.
 */

export const EARNED_HEADING = 'Commission earned';

export const EARNED_LABELS = {
  personal: 'Personal commission',
  regional: 'Regional bonus',
  payout: 'Payout',
  model: 'Model',
  gateMet: 'Gate met',
  gateNotMet: 'Gate not met',
} as const;

export const DERIVED_TARGET_LABEL = 'Target (derived)';

export function isRemainder(progress: CommissionProgress): boolean {
  return progress.kind === 'remainder';
}

/** True when the response carries an earned block (null or absent means none). */
export function hasEarned(
  progress: CommissionProgress,
): progress is CommissionProgress & { earned: CommissionEarned } {
  return progress.earned !== null && progress.earned !== undefined;
}

export interface EarnedRow {
  key: 'personal' | 'regional' | 'newBusiness' | 'payout';
  label: string;
  /** Decimal string in the scheme currency. */
  amount: string;
  /** 'Gate not met' on the regional row when the gate failed; otherwise null. */
  note: string | null;
}

/**
 * Personal commission, regional bonus (after the gate), new business commission
 * and payout; [] when nothing is earned. `payout` already includes the new
 * business commission. Its row appears only when the server sends
 * `newBusinessCommission` AND either the scheme's new business rate (when the
 * caller knows it; the progress row does not carry it) is above zero, or, with
 * the rate unknown, the commission itself is above zero. Rate-0 schemes and an
 * older API (key undefined) render exactly as before.
 */
export function earnedRows(
  progress: CommissionProgress,
  newBusinessRatePercent?: string | null,
): EarnedRow[] {
  if (!hasEarned(progress)) return [];
  const earned = progress.earned;
  const sent: string | undefined = earned.newBusinessCommission;
  const newBusinessCommission =
    typeof sent === 'string' &&
    (typeof newBusinessRatePercent === 'string'
      ? isPositive(newBusinessRatePercent)
      : isPositive(sent))
      ? sent
      : undefined;
  return [
    { key: 'personal', label: EARNED_LABELS.personal, amount: earned.personalCommission, note: null },
    {
      key: 'regional',
      label: EARNED_LABELS.regional,
      amount: earned.regionalPool,
      note: earned.gateMet ? null : EARNED_LABELS.gateNotMet,
    },
    ...(typeof newBusinessCommission === 'string'
      ? [
          {
            key: 'newBusiness' as const,
            label: NEW_BUSINESS_LABELS.newBusinessCommission,
            amount: newBusinessCommission,
            note: null,
          },
        ]
      : []),
    { key: 'payout', label: EARNED_LABELS.payout, amount: earned.payout, note: null },
  ];
}

/*
 * New business (owner 2026-10-28): per account, max(0, this period − the same
 * account's previous period), split across the people credited on it. The
 * server computes every figure; nothing here recomputes or sums them.
 * `undefined` on `newBusiness` means an older API; `null` means none.
 */

export const NEW_BUSINESS_LABELS = {
  accountsThisPeriod: 'Accounts this period',
  accountsLastPeriod: 'Same accounts last period',
  newBusiness: 'New business',
  newBusinessCommission: 'New business commission',
} as const;

export type CommissionProgressNewBusinessBlock = NonNullable<CommissionProgress['newBusiness']>;

/** True when the response carries a new business block (null or absent means none). */
export function hasNewBusiness(
  progress: CommissionProgress,
): progress is CommissionProgress & { newBusiness: CommissionProgressNewBusinessBlock } {
  return progress.newBusiness !== null && progress.newBusiness !== undefined;
}

/** Contract name for the same helper. */
export const earnedLines = earnedRows;

export function gateLabel(gateMet: boolean): string {
  return gateMet ? EARNED_LABELS.gateMet : EARNED_LABELS.gateNotMet;
}

export function modelLabel(model: CommissionEarned['model']): 'Offset' | 'Top-up' {
  return model === 'offset' ? 'Offset' : 'Top-up';
}

/** Label of the personal bar: the derived remainder target, else the commission personal target. */
export function personalTargetLabel(progress: CommissionProgress): string {
  return isRemainder(progress) ? DERIVED_TARGET_LABEL : PROGRESS_LABELS.personal;
}

/** A fact about the data: invoices left out for lack of an exchange rate. Null for none. */
export function excludedNote(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count === 1
    ? '1 invoice left out: no exchange rate for its bill date.'
    : `${count} invoices left out: no exchange rate for their bill dates.`;
}
